import { axiosInstance } from 'boot/axios';
import Declaration from 'src/models/declaration.js';
import AttachmentReadability from 'src/models/attachmentReadability.js';
import isEmpty from 'lodash/isEmpty';
import Customer from 'src/models/customer';
import DeclarationDetails from 'src/models/declarationDetails';
import { formatDateWithoutTime } from 'src/utils/format';

const DATE_FORMAT = 'yyyy-MM-dd';

export function getDeclarationsPreview({ commit, rootState }) {
  if (!rootState.user.permissions.mayReadDeclaration) return;
  commit('setIsLoadingDeclarationsPreview', true);
  return axiosInstance
    .get('Declaration/GetDeclarationsPreview')
    .then((response) => {
      if (!isEmpty(response.data)) {
        commit(
          'setDeclarationsPreview',
          response.data.map((declaration) => new Declaration(declaration)),
        );
      }
    })
    .finally(() => commit('setIsLoadingDeclarationsPreview', false));
}
export function getDeclarations({ commit }, payload) {
  commit('setIsLoadingDeclarations', true);
  return axiosInstance
    .get(`Declaration/GetDeclarations?customerNumber=${payload.customerNumber}&year=${payload.year}`)
    .then((response) => {
      commit(
        'setDeclarations',
        response.data.map((declaration) => new Declaration(declaration)),
      );
    })
    .finally(() => commit('setIsLoadingDeclarations', false));
}
export function getDeclarationDetails(_, payload) {
  const recordIdParam = payload.recordId ? `&recordId=${payload.recordId}` : '';
  return axiosInstance.get(`Declaration?declarationId=${payload.declarationId}${recordIdParam}`).then((response) => {
    return new DeclarationDetails(response.data);
  });
}

export function getAllDeclarations({ commit, state }) {
  if (state.declarations?.length > 0) return;
  commit('setIsLoadingDeclarations', true);
  return axiosInstance
    .get(`Declaration/ReadAll`)
    .then((response) => {
      commit(
        'setDeclarations',
        response.data.map((declaration) => new Declaration(declaration)),
      );
    })
    .finally(() => commit('setIsLoadingDeclarations', false));
}

export function clearDeclarations({ commit }) {
  commit('clearDeclarations');
}

export function clearAttachmentReadability({ commit }) {
  commit('clearAttachmentReadability');
}
export function setDeclarationForm({ commit }, payload) {
  commit('setDeclarationForm', payload);
}

export function saveDeclarationForm({ commit }, payload) {
  commit('setIsSavingDeclarations', true);
  const isTransportCosts = payload.healthcareCostValue.key === 'travel-costs';
  const isAbroad = !isTransportCosts ? payload.costLocationValue.key === 'abroad' : null;

  const formData = new FormData();
  formData.set('CurrentSelectedCustomerNumber', payload.selectedCustomer.customerNumber);
  formData.set('IsTransportCosts', isTransportCosts);

  if (isAbroad != null) {
    formData.set('IsAbroad', isAbroad);
  }

  if (isAbroad) {
    formData.set('Abroad.CountryCode', payload.country.key);
    formData.set('Abroad.CostsFrom', formatDateWithoutTime(payload.fromDate, DATE_FORMAT));
    formData.set('Abroad.CostsTo', formatDateWithoutTime(payload.toDate, DATE_FORMAT));
    formData.set('Abroad.CurrencyCode', payload.costCurrency.cc);
    formData.set('Abroad.Cost', payload.cost);
    formData.set('Abroad.Description', payload.moreTreatmentInfoValue);
    formData.set('Abroad.HadPreDeterminedCondition', payload.knownValue.key === 'yes');

    const hasTravelInsurance = payload.travelInsurance.hasInsurance.key === 'yes';
    formData.set('Abroad.HadMedicalTravelInsurance', hasTravelInsurance);
    if (hasTravelInsurance) {
      formData.set('Abroad.InsuranceCompany', payload.travelInsurance.insurerName);
      formData.set('Abroad.PolicyNumber', payload.travelInsurance.policyNumber);
    }

    formData.set('Abroad.WasThirdPartyInvolved', payload.involvedOthers.key === 'yes');
  } else if (isTransportCosts) {
    formData.set('Transport.TreatmentType', payload.treatmentType.key);
    formData.set('Transport.TransportType', payload.travelType.key);
    setFormAddress(formData, 'Transport.FromAddress', payload.addresses.from);
    setFormAddress(formData, 'Transport.ToAddress', payload.addresses.to);

    for (var i = 0; i < payload.trips.length; i++) {
      formData.set(`Transport.Rides[${i}].Date`, formatDateWithoutTime(payload.trips[i].date, DATE_FORMAT));
      formData.set(`Transport.Rides[${i}].IsSingleTrip`, payload.trips[i].isOneWay);
      if (payload.trips[i].additionalCost.type) {
        formData.set(`Transport.Rides[${i}].ExtraCostType`, payload.trips[i].additionalCost.type);
      }
      if (payload.trips[i].additionalCost.amount) {
        formData.set(`Transport.Rides[${i}].ExtraCost`, payload.trips[i].additionalCost.amount);
      }
    }
  }

  if (payload.documents && payload.documents.length > 0) {
    payload.documents.forEach((file) => formData.append('files', file.file));
  }

  return axiosInstance.post('Declaration', formData).finally(() => commit('setIsSavingDeclarations', false));
}

export function validateAttachmentsReadability({ commit }, payload) {
  const formData = new FormData();

  if (payload.files && payload.files.length > 0) {
    payload.files.forEach((file) => formData.append('files', file));
  }
  return axiosInstance.post('Declaration/ValidateAttachmentsReadability', formData).then((response) => {
    if (!isEmpty(response.data)) {
      commit(
        'setAttachmentReadability',
        response.data.map((attachmentReadability) => new AttachmentReadability(attachmentReadability)),
      );
    }
  });
}

export function clearDeclarationForm({ commit }) {
  commit('clearDeclarationForm');
}

export function getAllCustomersWithPolicyForYear({ commit }, payload) {
  commit('setIsLoadingCustomers', true);
  return axiosInstance
    .get(`Customer/GetAllCustomersWithPolicyForYear?year=${payload.year}&checkForOffer=${payload.checkForOffer}`)
    .then((response) => {
      if (!isEmpty(response.data)) {
        commit(
          'setCustomers',
          response.data.map((customer) => new Customer(customer)),
        );
      }
    })
    .finally(() => commit('setIsLoadingCustomers', false));
}

export function clearCustomers({ commit }) {
  commit('clearCustomers');
}

function setFormAddress(formData, startNameAddress, address) {
  if (address.name) {
    formData.set(startNameAddress + '.DestinationName', address.name);
  }
  formData.set(startNameAddress + '.CountryCode', address.country.key);
  formData.set(startNameAddress + '.ZipCode', address.postalCode);
  formData.set(startNameAddress + '.HouseNumber', address.houseNumber);
  if (address.addition) {
    formData.set(startNameAddress + '.HouseNumberAddition', address.addition);
  }
  formData.set(startNameAddress + '.Street', address.street);
  formData.set(startNameAddress + '.Location', address.city);
  formData.set(startNameAddress + '.IsHome', address.homeAddress);
}
